import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import client, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, Card, Btn, Field, Select, Input, Empty, ModeBadge } from "@/components/kit";
import { Youtube, Loader2, Wand2, Repeat, Save, Film, Clapperboard } from "lucide-react";

const FORMATS = ["Vídeo tradicional", "YouTube Short"];
const DURATIONS = ["5 minutos", "8 minutos", "10 minutos", "15 minutos"];
const REPURPOSE = ["YouTube Shorts", "TikTok", "Reels", "Post Feed", "Carrossel", "Stories", "Google Business", "WhatsApp"];

export default function YouTubeStudio() {
  const { activeOrgId } = useAuth();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [theme, setTheme] = useState("");
  const [format, setFormat] = useState(FORMATS[0]);
  const [duration, setDuration] = useState(DURATIONS[1]);
  const [busy, setBusy] = useState(false);
  const [project, setProject] = useState(null);
  const [targets, setTargets] = useState([]);
  const [thumbUrl, setThumbUrl] = useState(null);
  const [thumbId, setThumbId] = useState(null);
  const [thumbBusy, setThumbBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!activeOrgId) return;
    client.get("/brands").then((r) => { setBrands(r.data); if (r.data[0]) { setBrandId(r.data[0].id); setVidBrandId((prev) => prev || r.data[0].id); } });
  }, [activeOrgId]);

  const generate = async () => {
    if (!brandId || !theme.trim()) return toast.error("Selecione a marca e informe o tema");
    setBusy(true); setProject(null); setThumbUrl(null); setThumbId(null);
    try {
      const { data } = await client.post("/youtube/script", { brand_id: brandId, theme, format, duration });
      setProject(data);
      data.mode === "REAL" ? toast.success("Roteiro gerado com IA") : toast.warning(`Gerado em modo ${data.mode}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const data = project?.data || {};

  const genThumb = async () => {
    if (!theme.trim()) return toast.error("Informe o tema");
    setThumbBusy(true);
    try {
      const prompt = `Thumbnail de vídeo do YouTube 16:9 sobre "${data.thumbnail_idea || theme}". Design chamativo, alto contraste, foco no rosto/tema, sem texto ilegível.`;
      const { data: media } = await client.post("/ai/image", { prompt, category: "Thumbnails", brand_id: brandId });
      setThumbId(media.id);
      const blob = await client.get(`/media/${media.id}/file`, { responseType: "blob" });
      setThumbUrl(URL.createObjectURL(blob.data));
      toast.success("Thumbnail gerada com IA");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setThumbBusy(false); }
  };

  const saveAsPost = async () => {
    if (!project?.id) return;
    setSaving(true);
    try {
      const { data: r } = await client.post("/youtube/save-as-post", { project_id: project.id, thumbnail_media_id: thumbId });
      toast.success(`Post de ${r.channel} salvo na Central de Aprovação`);
      navigate("/app/aprovacao");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const [vidTheme, setVidTheme] = useState("");
  const [vidBrandId, setVidBrandId] = useState("");
  const [vidFormat, setVidFormat] = useState("vertical");
  const [vidMode, setVidMode] = useState("fast");
  const [vidDuration, setVidDuration] = useState(60);
  const [vidVoice, setVidVoice] = useState("male");
  const [vidMusic, setVidMusic] = useState("emocional");
  const [vidMusicPrompt, setVidMusicPrompt] = useState("");
  const [vidJob, setVidJob] = useState(null);
  const [vidJobId, setVidJobId] = useState(null);
  const [vidBusy, setVidBusy] = useState(false);
  const pollRef = useRef(null);
  const token = localStorage.getItem("pf_token");

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const pollVideo = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let errs = 0;
    pollRef.current = setInterval(async () => {
      try {
        const { data: j } = await client.get(`/video/jobs/${jobId}`);
        errs = 0;
        setVidJob(j);
        if (j.status === "DONE") { clearInterval(pollRef.current); setVidBusy(false); toast.success("Vídeo gerado com IA!"); }
        else if (j.status === "ERROR") { clearInterval(pollRef.current); setVidBusy(false); toast.error(`Falha: ${j.error}`); }
        else if (j.status === "CANCELLED") { clearInterval(pollRef.current); setVidBusy(false); }
      } catch {
        errs += 1;
        // Erros 502/520 intermitentes do proxy durante o processamento pesado (Veo) são
        // transitórios — seguimos tentando por bem mais tempo. O job continua no servidor.
        if (errs >= 60) {
          clearInterval(pollRef.current); setVidBusy(false);
          toast.info("O vídeo continua sendo gerado no servidor. Ele aparecerá na Central de Aprovação quando ficar pronto — pode fechar esta tela.");
        }
      }
    }, 3000);
  };

  // Retoma o acompanhamento de um job em andamento (ou mostra o recém-concluído) ao voltar à página
  useEffect(() => {
    if (!activeOrgId) return;
    client.get("/video/jobs").then(({ data }) => {
      const j = data && data[0];
      if (!j) return;
      const running = !["DONE", "ERROR", "CANCELLED"].includes(j.status);
      const ts = new Date(j.updated_at || j.created_at || 0).getTime();
      const recentDone = j.status === "DONE" && Date.now() - ts < 30 * 60 * 1000;
      if (running || recentDone) {
        setVidJob(j);
        setVidJobId(j.id);
        if (j.brand_id) setVidBrandId(j.brand_id);
        if (j.theme) setVidTheme(j.theme);
        if (j.orientation) setVidFormat(j.orientation);
        if (j.duration) setVidDuration(j.duration);
      }
      if (running) { setVidBusy(true); pollVideo(j.id); }
    }).catch(() => {});
  }, [activeOrgId]);

  const startVideo = async () => {
    if (!vidBrandId || !vidTheme.trim()) return toast.error("Selecione a marca e informe o tema");
    setVidBusy(true); setVidJob({ status: "QUEUED", progress: 0, step: "Na fila" }); setVidJobId(null);
    try {
      const { data } = await client.post("/video/generate", { brand_id: vidBrandId, theme: vidTheme, format: vidFormat, mode: vidMode, duration: vidDuration, voice: vidVoice, music_style: vidMusic, music_prompt: vidMusicPrompt });
      setVidJobId(data.job_id);
      pollVideo(data.job_id);
    } catch (e) { setVidBusy(false); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resumeVideo = async () => {
    if (!vidJobId) return;
    setVidBusy(true); setVidJob({ status: "QUEUED", progress: 8, step: "Retomando..." });
    try {
      const { data } = await client.post(`/video/jobs/${vidJobId}/resume`);
      if (data.resumed) {
        toast.success(`Retomando o vídeo — reaproveitando ${data.reused_clips || 0} clipe(s) já gerado(s).`);
        pollVideo(vidJobId);
      } else {
        setVidBusy(false); toast.info(data.message || "Não foi possível retomar.");
      }
    } catch (e) { setVidBusy(false); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cancelVideo = async () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setVidBusy(false);
    try { if (vidJobId) await client.post(`/video/jobs/${vidJobId}/cancel`); } catch {}
    setVidJob(null);
    toast("Geração cancelada");
  };

  const pipeline = [
    ["Tema", !!theme], ["Roteiro", !!data.script], ["Descrição", !!data.description],
    ["Capítulos", !!(data.chapters?.length)], ["Thumbnail", !!data.thumbnail_idea],
    ["Tags", !!(data.tags?.length)], ["Transcrição", !!data.transcript_note],
  ];

  if (!brands.length) return <div><PageHeader title="YouTube Studio" /><Empty>Crie uma marca primeiro.</Empty></div>;

  return (
    <div>
      <PageHeader title="YouTube Studio" subtitle="Título, roteiro, descrição, capítulos, tags, thumbnail e transcrição — Shorts e vídeos longos."
        actions={<span className="hidden sm:inline"><ModeBadge mode="REAL" /> <span className="text-xs text-slate-400 ml-1">Roteiro IA</span></span>} />

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="space-y-4">
          <h3 className="font-display font-bold flex items-center gap-2"><Youtube size={18} className="text-rose-600" /> Novo vídeo</h3>
          <Field label="Marca"><Select data-testid="yt-brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>{brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</Select></Field>
          <Field label="Tema do vídeo"><Input data-testid="yt-theme" value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="Ex.: como apoiar um familiar" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Formato"><Select data-testid="yt-format" value={format} onChange={(e) => setFormat(e.target.value)}>{FORMATS.map((f) => <option key={f}>{f}</option>)}</Select></Field>
            <Field label="Duração"><Select data-testid="yt-duration" value={duration} onChange={(e) => setDuration(e.target.value)}>{DURATIONS.map((d) => <option key={d}>{d}</option>)}</Select></Field>
          </div>
          <Btn data-testid="yt-generate-btn" className="w-full" onClick={generate} disabled={busy}>{busy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} Gerar roteiro</Btn>
        </Card>

        <Card>
          <h3 className="font-display font-bold mb-4">Pipeline</h3>
          <ul className="space-y-2.5">
            {pipeline.map(([label, done]) => (
              <li key={label} className="flex items-center gap-2 text-sm">
                <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[11px] ${done ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{done ? "✓" : "○"}</span>
                <span className={done ? "text-slate-800" : "text-slate-400"}>{label}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="lg:col-span-1">
          <h3 className="font-display font-bold mb-3">Resultado</h3>
          {project ? (
            <div className="space-y-3 text-sm" data-testid="yt-result">
              <div><div className="text-xs text-slate-400 uppercase">Título</div><div className="font-semibold text-slate-800">{data.title}</div></div>
              <div><div className="text-xs text-slate-400 uppercase">Descrição</div><p className="text-slate-600 line-clamp-3">{data.description}</p></div>
              {data.tags?.length ? <div className="flex flex-wrap gap-1.5">{data.tags.slice(0, 8).map((t, i) => <span key={i} className="text-[11px] bg-slate-100 rounded-full px-2 py-0.5 text-slate-600">#{t}</span>)}</div> : null}
              <details><summary className="cursor-pointer text-[#3457d5] text-xs font-medium">Ver roteiro completo</summary><p className="text-slate-600 mt-2 whitespace-pre-wrap text-xs max-h-48 overflow-y-auto">{data.script}</p></details>
              <div className="border-t border-slate-100 pt-3">
                <div className="text-xs text-slate-400 uppercase mb-2">Thumbnail</div>
                {thumbUrl && <img src={thumbUrl} alt="thumbnail" className="w-full rounded-lg border border-slate-200 mb-2" data-testid="yt-thumb-preview" />}
                <Btn variant="secondary" className="w-full" data-testid="yt-thumb-btn" onClick={genThumb} disabled={thumbBusy}>{thumbBusy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Gerar thumbnail com IA</Btn>
              </div>
              <div className="border-t border-slate-100 pt-3 space-y-2">
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-2.5 text-[11px] text-amber-700">
                  Isto gera o <b>roteiro, título, descrição e thumbnail</b> — não gera o vídeo em si. Salve como post e, na Central de Aprovação, <b>anexe o vídeo gravado</b> para publicar no YouTube.
                </div>
                <Btn className="w-full" data-testid="yt-save-post-btn" onClick={saveAsPost} disabled={saving}>{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Salvar na Central de Aprovação</Btn>
              </div>
            </div>
          ) : <p className="text-sm text-slate-400">Gere um roteiro para ver o resultado aqui.</p>}
        </Card>
      </div>

      <Card className="mt-6 border-2 border-[#3457d5]/20">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <h3 className="font-display font-bold flex items-center gap-2"><Clapperboard size={18} className="text-[#3457d5]" /> Gerar vídeo narrado com IA</h3>
          <ModeBadge mode="REAL" glow />
        </div>
        <p className="text-sm text-slate-500 mb-4">A IA cria o vídeo completo a partir do tema: roteiro por cenas + <b>narração em voz (pt-BR)</b>. Escolha o estilo: <b>Rápido</b> (imagens de IA + narração, montado no nosso servidor — sempre disponível) ou <b>Premium (movimento)</b> (clipes com movimento real via <b>Google Veo 3.1</b> + narração pt-BR). Salvo na Central de Aprovação.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <Field label="Marca / Clínica (base do conteúdo)">
              <Select data-testid="vid-brand" value={vidBrandId} onChange={(e) => setVidBrandId(e.target.value)}>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Select>
            </Field>
            {(() => {
              const b = brands.find((x) => x.id === vidBrandId);
              if (!b) return null;
              const wa = b.whatsapp || b.phone;
              return (
                <div className="-mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-500" data-testid="vid-brand-summary">
                  Baseado em <b className="text-slate-700">{b.name}</b>
                  {b.niche ? <> · {b.niche}</> : null}
                  {wa ? <> · WhatsApp <b className="text-emerald-600">{wa}</b></> : <> · <span className="text-amber-600">sem WhatsApp cadastrado</span></>}
                </div>
              );
            })()}
            <Field label="Tema do vídeo"><Input data-testid="vid-theme" value={vidTheme} onChange={(e) => setVidTheme(e.target.value)} placeholder="Ex.: 3 atitudes diárias para evitar recaída" /></Field>
            <Field label="Formato">
              <Select data-testid="vid-format" value={vidFormat} onChange={(e) => setVidFormat(e.target.value)}>
                <option value="vertical">Vertical 9:16 (TikTok / Shorts / Reels)</option>
                <option value="horizontal">Horizontal 16:9 (YouTube)</option>
              </Select>
            </Field>
            <Field label="Duração do vídeo">
              <Select data-testid="vid-duration" value={vidDuration} onChange={(e) => setVidDuration(Number(e.target.value))}>
                <option value={30}>30 segundos</option>
                <option value={60}>60 segundos (1 min)</option>
                <option value={120}>120 segundos (2 min)</option>
              </Select>
            </Field>
            <Field label="Estilo do vídeo">
              <Select data-testid="vid-mode" value={vidMode} onChange={(e) => setVidMode(e.target.value)}>
                <option value="fast">Rápido — imagens IA + narração (montado no servidor, sempre disponível)</option>
                <option value="cinematic">Premium (movimento) — Google Veo 3.1 (movimento real, ~8s por cena)</option>
              </Select>
            </Field>
            <Field label="Voz da narração">
              <Select data-testid="vid-voice" value={vidVoice} onChange={(e) => setVidVoice(e.target.value)}>
                <option value="male">Masculina (voz grave)</option>
                <option value="female">Feminina</option>
              </Select>
            </Field>
            <Field label="Música de fundo">
              <Select data-testid="vid-music" value={vidMusic} onChange={(e) => setVidMusic(e.target.value)}>
                <option value="emocional">Calma / emocional</option>
                <option value="esperancosa">Esperançosa / positiva</option>
                <option value="corporativa">Corporativa / clean</option>
                <option value="cinematografica">Cinematográfica / intensa</option>
                <option value="nenhuma">Sem música</option>
              </Select>
            </Field>
            {vidMusic !== "nenhuma" && (
              <Field label="Descrever a música (opcional — IA cria a trilha)">
                <Input data-testid="vid-music-prompt" value={vidMusicPrompt} onChange={(e) => setVidMusicPrompt(e.target.value)} placeholder='Ex.: piano suave e esperançoso com cordas leves' />
                <p className="text-[11px] text-slate-400 mt-1">Se preencher, a IA tenta criar a trilha a partir do texto (requer plano pago do ElevenLabs Music). Sem isso, usamos o estilo escolhido acima.</p>
              </Field>
            )}
            <div className="flex gap-2">
              <Btn className="flex-1" data-testid="vid-generate-btn" onClick={startVideo} disabled={vidBusy}>
                {vidBusy ? <Loader2 size={16} className="animate-spin" /> : <Film size={16} />} {vidBusy ? "Gerando vídeo..." : "Gerar vídeo com IA"}
              </Btn>
              {vidBusy && (
                <Btn variant="secondary" data-testid="vid-cancel-btn" onClick={cancelVideo}>Cancelar</Btn>
              )}
            </div>
            <p className="text-[11px] text-slate-400">Modo Rápido: montado no nosso servidor, disponível sempre (não depende de serviços externos). Premium (movimento): usa o Google Veo 3.1 para gerar movimento real por cena (leva alguns minutos e consome créditos do seu projeto Google). Pode fechar depois — o vídeo aparece na Central de Aprovação.</p>
          </div>
          <div>
            {vidJob && vidJob.status !== "DONE" && vidJob.status !== "ERROR" && vidJob.status !== "CANCELLED" && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="vid-progress">
                <div className="flex items-center gap-2 text-sm text-slate-700 mb-2"><Loader2 size={15} className="animate-spin text-[#3457d5]" /> {vidJob.step || "Processando..."}</div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200"><div className="h-full pf-gradient transition-all" style={{ width: `${vidJob.progress || 0}%` }} /></div>
                <div className="text-[11px] text-slate-400 mt-1">{vidJob.progress || 0}%</div>
              </div>
            )}
            {vidJob && vidJob.status === "CANCELLED" && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-500" data-testid="vid-cancelled">Geração cancelada. Você pode gerar novamente.</div>
            )}
            {vidJob && vidJob.status === "ERROR" && (
              <div className="rounded-xl bg-rose-50 border border-rose-100 p-4 text-sm text-rose-600 space-y-3" data-testid="vid-error">
                <div>Falha ao gerar: {vidJob.error}</div>
                {vidJobId && (
                  <Btn variant="primary" className="w-full" data-testid="vid-resume-btn" onClick={resumeVideo}>
                    Tentar novamente (reaproveita os clipes já gerados)
                  </Btn>
                )}
              </div>
            )}
            {vidJob && vidJob.status === "DONE" && vidJob.media_id && (
              <div className="space-y-2" data-testid="vid-result">
                <video src={`${API}/media/${vidJob.media_id}/file?auth=${token}`} controls preload="metadata" className={`w-full rounded-lg bg-black ${vidFormat === "vertical" ? "aspect-[9/16] max-h-[420px]" : "aspect-video"} object-contain mx-auto`} data-testid="vid-preview" />
                <Btn variant="secondary" className="w-full" data-testid="vid-goto-approval" onClick={() => navigate("/app/aprovacao")}>Ver na Central de Aprovação</Btn>
              </div>
            )}
            {!vidJob && <div className="rounded-xl bg-slate-50 border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400 h-full flex items-center justify-center">O vídeo gerado aparece aqui.</div>}
          </div>
        </div>
      </Card>

      <Card className="mt-6">
        <h3 className="font-display font-bold mb-2 flex items-center gap-2"><Repeat size={18} className="text-[#3457d5]" /> Content Repurposing</h3>
        <p className="text-sm text-slate-500 mb-3">Reaproveite o vídeo em outros formatos e canais. (Cria variações estruturais do conteúdo)</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {REPURPOSE.map((t) => {
            const on = targets.includes(t);
            return <button key={t} data-testid={`repurpose-${t}`} onClick={() => setTargets(on ? targets.filter((x) => x !== t) : [...targets, t])}
              className={`rounded-full px-3 py-1.5 text-sm border transition-all ${on ? "pf-gradient text-white border-transparent" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{on ? "✓ " : ""}{t}</button>;
          })}
        </div>
        <p className="text-xs text-slate-400">Selecione conteúdos na Central de Aprovação para reaproveitar; aqui você define os alvos padrão.</p>
      </Card>
    </div>
  );
}
